import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardBody, Button } from "react-bootstrap";
import PillNavigation from "../../../../../../components/SubNavBar";
import { TabContent, TabPane } from "reactstrap";
import { NAV_LIST_CART_CONTROLLER_INVENTORY_VIEW } from "../../../../../../dummyData/FoodItemData";
import "./index.css";
import { useLocation } from "react-router-dom";
import ColumnChartComponent from "./ItemList/GraphChart";
import PieChartComponent from "./ItemList/PieChart";
import { useSelector } from "react-redux";
import { ArrowLeft } from "lucide-react";
import { useFirebaseCartSummaries } from "../../../../../../hooks/useFirebaseCartSummaries";

interface ProductData {
  id: string;
  displayName: string;
  productId: string;
  itemId: string;
  cartName: string;
  cartId: string;
  selectedDate: string;
}

const InventoryView = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { product, cartId, cartName, selectedDate } = location.state as {
    product: ProductData;
    cartId: string;
    cartName: string;
    selectedDate: string;
  };

  const [active, setActive] = useState(1);
  const restaurantId = useSelector(
    (state: any) => state.restaurant.selectedRestaurantDetails?.id,
  );

  const toggle = (tab: any) => {
    setActive(tab);
  };

  // Simplified back button handler
  const handleBack = () => {
    // Navigate back with the necessary state
    navigate("/carts/cart-list/view", {
      state: { 
        carts: { 
          id: cartId, 
          displayName: cartName 
        },
        activeTab: 2,
        selectedDate: selectedDate,
        _fromInventoryView: true
      },
      replace: false 
    });
  };

  // Use the cart summaries hook
  const {
    data: cartSummaries,
    loading,
    error,
  } = useFirebaseCartSummaries(restaurantId, new Date(selectedDate));

  // Find the current cart and product
  const currentCart = useMemo(() => {
    if (!cartSummaries) return null;
    return cartSummaries.find((cart) => cart.id === cartId);
  }, [cartSummaries, cartId]);

  const currentProduct = useMemo(() => {
    if (!currentCart || !currentCart.products) return null;
    return (
      Object.values(currentCart.products).find((p) => p.id === product.id) ||
      null
    );
  }, [currentCart, product.id]);

  const channelChartData = useMemo(() => {
    if (!currentProduct || !currentProduct.channels) return [];

    const data: any[] = [["Channel", "Available", "Sold", "Received"]]; 

    Object.entries(currentProduct.channels).forEach(
      ([channelName, channel]) => {
        const available = channel.available || 0;
        const sold = channel.sold || 0;
        const received = channel.received || 0;

        if (available > 0 || sold > 0 || received > 0) {
          data.push([channelName, available, sold, received]);
        }
      },
    );

    return data.length > 1 ? data : [];
  }, [currentProduct]);

  const pieChartData = useMemo(() => {
    if (!currentProduct || !currentProduct.channels) return [];

    let totalAvailable = 0;
    let totalSold = 0;
    let totalReserved = 0;
    let totalReceived = 0;

    Object.values(currentProduct.channels).forEach((channel) => {
      totalAvailable += channel.available || 0;
      totalSold += channel.sold || 0;
      totalReserved += channel.reserved || 0;
      totalReceived += channel.received || 0; 
    });

    return [
      ["Status", "Quantity"],
      ["Available", totalAvailable],
      ["Sold", totalSold],
      ["Reserved", totalReserved],
      ["Received", totalReceived],
    ];
  }, [currentProduct]);

  const channelDetails = useMemo(() => {
    if (!currentProduct || !currentProduct.channels) return [];

    return Object.entries(currentProduct.channels).map(
      ([channelName, channel]) => ({
        channelName,
        available: channel.available || 0,
        sold: channel.sold || 0,
        reserved: channel.reserved || 0,
        allocated: channel.allocated || 0,
        cancelled: channel.cancelled || 0,
        produced: channel.produced || 0,
        received: channel.received || 0, 
      }),
    );
  }, [currentProduct]);

  if (loading) {
    return (
      <>
        <div className="mb-3">
          <Button 
            variant="outline-secondary" 
            onClick={handleBack}
            className="d-flex align-items-center gap-2"
          >
            <ArrowLeft /> Back to Cart Inventory
          </Button>
        </div>
        <Card>
          <CardBody>
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-3">Loading product data...</p>
            </div>
          </CardBody>
        </Card>
      </>
    );
  }

  if (error) {
    return (
      <>
        <div className="mb-3">
          <Button 
            variant="outline-secondary" 
            onClick={handleBack}
            className="d-flex align-items-center gap-2"
          >
            <ArrowLeft /> Back to Cart Inventory
          </Button>
        </div>
        <Card>
          <CardBody>
            <div className="alert alert-danger">
              <h5>Error Loading Data</h5>
              <p>{error.message}</p>
            </div>
          </CardBody>
        </Card>
      </>
    );
  }

  if (!currentProduct) {
    return (
      <>
        <div className="mb-3">
          <Button 
            variant="outline-secondary" 
            onClick={handleBack}
            className="d-flex align-items-center gap-2"
          >
            <ArrowLeft /> Back to Cart Inventory
          </Button>
        </div>
        <Card>
          <CardBody>
            <div className="alert alert-warning">
              <h5>Product Not Found</h5>
              <p>Could not find product data for the selected item.</p>
            </div>
          </CardBody>
        </Card>
      </>
    );
  }

  return (
    <>
      <div className="mb-3">
        <Button 
          variant="outline-secondary" 
          onClick={handleBack}
          className="d-flex align-items-center gap-2"
        >
          <ArrowLeft /> Back to Cart Inventory
        </Button>
      </div>

      <Card>
        <CardBody>
          <div>
            <div className="d-flex justify-content-between align-items-start">
              <div>
                <h3 className="mb-2">{currentProduct.displayName}</h3>
                <div className="row">
                  <div className="col-md-8">
                    <p className="mb-1">
                      <strong>Product ID:</strong> {currentProduct.productId}
                    </p>
                    <p className="mb-1">
                      <strong>Cart:</strong> {cartName}
                    </p>
                    <p>
                      <strong>Date:</strong> {selectedDate}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <PillNavigation
            navList={NAV_LIST_CART_CONTROLLER_INVENTORY_VIEW}
            active={active}
            toggle={toggle}
          />
          <TabContent activeTab={active}>
            {active === 1 && (
              <div className="sub-nav-bar-each-part">
                <TabPane tabId={1}>
                  {channelChartData.length > 1 ? (
                    <ColumnChartComponent
                      data={channelChartData}
                      title="Channel-wise Inventory Status"
                    />
                  ) : (
                    <div className="text-center py-5">
                      <p className="text-muted">
                        No channel data available for this product
                      </p>
                    </div>
                  )}
                </TabPane>
              </div>
            )}
          </TabContent>
        </CardBody>
      </Card>

      {active === 1 && (
        <PieChartComponent
          data={pieChartData}
          channelDetails={channelDetails}
          product={currentProduct}
        />
      )}
    </>
  );
};

export default InventoryView;